import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import { useEffect, useRef, useState } from "react";
import { formatCrypto } from "../../utils";
import { XMarkIcon } from "@heroicons/react/24/solid";

// Helper hook for clicking outside an element (re-defined here for clarity with SelectPairModal)
const useOnClickOutsideModal = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
    };
  }, [ref, handler]);
};

const SelectPairModal = ({
  isOpen,
  onClose,
  onSelectCoin,
  coinOptions,
  selectedCoin,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const modalRef = useRef(null);

  useOnClickOutsideModal(modalRef, () => {
    // Use the specific modal hook
    if (isOpen) {
      onClose();
    }
  });

  const filteredCoins = coinOptions.filter(
    (coin) =>
      coin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coin.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Render only when it's supposed to be open or during transition
  if (!isOpen && !modalRef.current) return null;

  return (
    <div
      className={`fixed inset-0 bg-gray-500/55 flex items-end backdrop-blur-sm justify-center z-[998] xl:hidden
        transform transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      onClick={onClose} // Close modal when clicking overlay
    >
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
        className={`
          bg-white rounded-t-2xl shadow-lg p-4 w-full max-h-[90vh]
          transform transition-transform duration-300 ease-out
          ${isOpen ? "translate-y-0" : "translate-y-full"}
          relative flex flex-col
          sm:max-w-md mx-auto // For wider mobile/tablet screens
        `}
      >
        {/* Header with Title and Close Button */}
        <div className="flex justify-between items-center pb-3 border-b border-gray-200 sticky top-0 bg-white z-10 -mx-4 px-4 pt-4">
          <h3 className="text-xl font-semibold text-gray-800">Pair</h3>
          <div
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <XMarkIcon className="h-6 w-6" />
          </div>
        </div>

        {/* Search Input */}
        <div className="py-3 sticky top-[60px] bg-white z-10 -mx-4 px-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search Coin Name"
              className="w-full p-3 py-2 pl-9  bg-[#F2F4F8] border-none rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
        </div>

        {/* Coin List Headers */}
        <div className="flex justify-between items-center text-xs text-gray-500 mt-3 mb-2 px-0">
          <span className="flex-1 pl-10">Pair</span>
          <span className="w-20 text-right">Price</span>
          <span className="w-16 text-right pr-2">24H</span>
        </div>

        {/* Coin List - Scrollable area */}
        <div
          className="flex-1 ml-[-1rem] !w-[calc(100%+1rem)] py-2 overflow-y-auto overflow-x-hidden"
          style={{ paddingBottom: "70px" }}
        >
          {filteredCoins.length > 0 ? (
            filteredCoins.map((coin) => (
              <a
                href="#"
                key={coin.id}
                onClick={(e) => {
                  e.preventDefault();
                  onSelectCoin(coin);
                }}
                className={`flex relative items-center gap-3 px-2 py-3 
                ${
                  selectedCoin.id === coin.id
                    ? "bg-[#F0F0FF] text-black font-medium after:absolute after:h-full after:w-[5px] after:bg-[#2D224E] after:left-0"
                    : "hover:bg-gray-100"
                }
                `}
              >
                <img
                  src={coin?.image}
                  alt={coin?.symbol}
                  className="w-6 h-6 rounded-full ml-2"
                />
                <span className="flex-1 font-medium pl-1 uppercase">
                  {coin?.symbol}
                </span>
                <span className="w-20 text-right">
                  {formatCrypto(coin?.current_price)}
                </span>
                <span
                  className={`w-16 text-right pr-2 ${
                    coin?.price_change_percentage_24h >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {formatCrypto(coin?.price_change_percentage_24h)}%
                </span>
              </a>
            ))
          ) : (
            <div className="text-center text-sm text-gray-500 py-4">
              No results found
            </div>
          )}
        </div>

        {/* Fixed "Select Pair" Button at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
          <button
            onClick={onClose} // This button will typically confirm selection
            className="w-full py-3 bg-[#2D224E] text-white font-bold rounded-lg hover:bg-purple-800 transition-colors"
          >
            Select Pair
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectPairModal;
